import { useState } from "react";
import { Trash2, Loader2, AlertOctagon, CheckCircle2, Shield } from "lucide-react";
import { platformPost } from "./api/platformClient";

const makeIdempotencyKey = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `cc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const COUNT_FIELDS = [
  { key: "company", label: "Company" },
  { key: "stores", label: "Stores" },
  { key: "departments", label: "Departments" },
  { key: "users", label: "Users" },
  { key: "password_reset_tokens", label: "Password reset tokens" },
  { key: "tenant_drafts", label: "Tenant drafts" },
  { key: "action_history_references", label: "Action history references" },
];

const readCounts = (resp) => {
  if (!resp || typeof resp !== "object") return null;
  const c = resp.counts || resp;
  const out = {};
  for (const f of COUNT_FIELDS) {
    if (typeof c[f.key] === "number") out[f.key] = c[f.key];
  }
  return out;
};

// Multi-step flow: warn → dry-run → final confirm → executing → success.
export const DeleteTestTenantFlow = ({ companyId, companyName, companyCode, onClose, onDeleted }) => {
  const expectedPhrase = `DELETE ${companyCode}`;
  const [step, setStep] = useState("warn"); // warn | dry-run | confirm | executing | success | failure
  const [typed, setTyped] = useState("");
  const [dryRunCounts, setDryRunCounts] = useState(null);
  const [deletedCounts, setDeletedCounts] = useState(null);
  const [error, setError] = useState(null);
  const [idempotencyKey] = useState(() => makeIdempotencyKey());

  const phraseOk = typed.trim() === expectedPhrase;

  const runDryRun = async () => {
    setError(null);
    setStep("dry-run");
    try {
      const resp = await platformPost(
        `/api/platform/companies/${companyId}/delete-test-tenant`,
        { dry_run: true }
      );
      setDryRunCounts(readCounts(resp));
      setStep("confirm");
    } catch (e) {
      setError(e);
      setStep("failure");
    }
  };

  const executeDelete = async () => {
    setError(null);
    setStep("executing");
    try {
      const resp = await platformPost(
        `/api/platform/companies/${companyId}/delete-test-tenant`,
        {
          dry_run: false,
          confirm: expectedPhrase,
          idempotency_key: idempotencyKey,
        }
      );
      setDeletedCounts(readCounts(resp) || dryRunCounts);
      setStep("success");
    } catch (e) {
      setError(e);
      setStep("failure");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm" data-testid="delete-test-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <Trash2 className="h-5 w-5 text-rose-600" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">Delete test tenant — {companyName}</h3>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{companyCode}</p>
          </div>
        </div>

        {step === "warn" && (
          <WarnStep
            expectedPhrase={expectedPhrase}
            typed={typed}
            setTyped={setTyped}
            phraseOk={phraseOk}
            onCancel={onClose}
            onContinue={runDryRun}
          />
        )}

        {step === "dry-run" && (
          <div className="px-6 py-10 text-center" data-testid="delete-dry-running">
            <Loader2 className="h-6 w-6 text-slate-400 animate-spin mx-auto" />
            <p className="mt-3 text-sm text-slate-600">Asking ShelfLife to dry-run the deletion…</p>
          </div>
        )}

        {step === "confirm" && dryRunCounts && (
          <ConfirmStep
            counts={dryRunCounts}
            onBack={() => setStep("warn")}
            onExecute={executeDelete}
          />
        )}

        {step === "executing" && (
          <div className="px-6 py-10 text-center" data-testid="delete-executing">
            <Loader2 className="h-6 w-6 text-rose-600 animate-spin mx-auto" />
            <p className="mt-3 text-sm text-slate-700">Deleting tenant permanently…</p>
            <p className="text-xs text-slate-500 mt-1">Do not close this window.</p>
          </div>
        )}

        {step === "success" && (
          <SuccessStep
            companyName={companyName}
            counts={deletedCounts}
            onDone={() => { onDeleted && onDeleted(); onClose && onClose(); }}
          />
        )}

        {step === "failure" && (
          <FailureStep
            error={error}
            onCancel={onClose}
            onRetry={() => { setError(null); setStep("warn"); }}
          />
        )}
      </div>
    </div>
  );
};

const WarnStep = ({ expectedPhrase, typed, setTyped, phraseOk, onCancel, onContinue }) => (
  <>
    <div className="px-6 py-5 space-y-4 text-sm text-slate-700">
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900 flex items-start gap-2" data-testid="delete-warning">
        <Shield className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Permanent deletion.</p>
          <p className="text-xs mt-1">This will permanently remove the tenant, its stores, departments, users, password-reset tokens and tenant drafts. Action history is retained for audit. Action cannot be undone from the Control Center.</p>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
          Type this exact phrase to continue
        </label>
        <div className="font-mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 inline-block">
          {expectedPhrase}
        </div>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          data-testid="delete-typed-input"
          className="mt-2 w-full font-mono text-sm rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
          placeholder={`Type ${expectedPhrase}`}
        />
        {typed && !phraseOk && (
          <p className="text-xs text-slate-500 mt-1">Phrase must match exactly (case-sensitive).</p>
        )}
      </div>
    </div>
    <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
      <button
        type="button"
        data-testid="delete-cancel"
        onClick={onCancel}
        className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
      >Cancel</button>
      <button
        type="button"
        data-testid="delete-run-dry-run"
        onClick={onContinue}
        disabled={!phraseOk}
        className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Run dry-run</button>
    </div>
  </>
);

const ConfirmStep = ({ counts, onBack, onExecute }) => (
  <>
    <div className="px-6 py-5 text-sm text-slate-700">
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Dry-run from ShelfLife</p>
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="delete-dry-run-counts">
        {COUNT_FIELDS.map((f) => (
          <CountRow key={f.key} label={f.label} value={counts[f.key]} testId={`delete-count-${f.key}`} />
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">
        Action history references will be retained for audit. Deletion is irreversible from the Control Center.
      </p>
    </div>
    <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
      <button
        type="button"
        data-testid="delete-back"
        onClick={onBack}
        className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
      >Back</button>
      <button
        type="button"
        data-testid="delete-execute"
        onClick={onExecute}
        className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 inline-flex items-center gap-2"
      >
        <Trash2 className="h-4 w-4" />
        Delete Permanently
      </button>
    </div>
  </>
);

const SuccessStep = ({ companyName, counts, onDone }) => (
  <div className="px-6 py-6 text-center" data-testid="delete-success">
    <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
    <h4 className="text-lg font-semibold text-slate-900">{companyName} deleted.</h4>
    <p className="text-xs text-slate-500 mt-1">Audit row retained on ShelfLife.</p>
    {counts && (
      <div className="mt-4 text-left rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="delete-success-counts">
        {COUNT_FIELDS.map((f) =>
          typeof counts[f.key] === "number"
            ? <CountRow key={f.key} label={f.label} value={counts[f.key]} testId={`delete-deleted-${f.key}`} />
            : null
        )}
      </div>
    )}
    <button
      type="button"
      data-testid="delete-success-done"
      onClick={onDone}
      className="mt-5 px-4 py-2 rounded-lg bg-[#0d3b66] text-white text-sm font-medium hover:bg-[#0a2c4d]"
    >Return to clients</button>
  </div>
);

const FailureStep = ({ error, onCancel, onRetry }) => (
  <div className="px-6 py-5" data-testid="delete-failure">
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900 flex items-start gap-2">
      <AlertOctagon className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold text-sm">Deletion failed.</p>
        <p className="text-xs mt-1">{error?.message || "Unknown error."}</p>
        {error?.payload && error.payload.detail && typeof error.payload.detail !== "string" && (
          <pre className="text-[10px] mt-2 bg-white/70 rounded p-2 max-h-32 overflow-auto">
            {JSON.stringify(error.payload.detail, null, 2)}
          </pre>
        )}
      </div>
    </div>
    <div className="mt-4 flex justify-end gap-2">
      <button
        type="button"
        data-testid="delete-failure-close"
        onClick={onCancel}
        className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
      >Close</button>
      <button
        type="button"
        data-testid="delete-failure-retry"
        onClick={onRetry}
        className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-700"
      >Start over</button>
    </div>
  </div>
);

const CountRow = ({ label, value, testId }) => (
  <div className="px-3 py-2 flex items-center justify-between text-sm" data-testid={testId}>
    <span className="text-slate-600">{label}</span>
    <span className="font-mono font-semibold text-slate-900">{value.toLocaleString()}</span>
  </div>
);
